import { signInWithRedirect } from "aws-amplify/auth";

/**
 * When an invitee picks "Continue with Google", Cognito's pre sign-up trigger
 * links their Google identity to the account that was pre-created for their
 * email. Cognito then still tries to create the `google_<sub>` profile it was
 * in the middle of making, finds it linked, and rejects that very first
 * sign-in with "Already found an entry for username google_…". The link
 * itself held: repeating the redirect signs the invitee in to the linked
 * account. Documented Cognito behaviour, see
 * https://repost.aws/questions/QUgWVkIodQS1W3Yj8MYjInbA
 */
const FIRST_SIGN_IN_AFTER_LINK = /already found an entry for username/i;

const RETRY_FLAG = "spectacle.googleSignInRetriedAfterLink";

export type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * Both the redirect URL (read on mount) and Amplify's `signInWithRedirect_failure`
 * Hub event report the same failure, and which one arrives first depends on
 * Amplify's timing. The first caller decides for the page load; later callers
 * get the same answer, so nobody flips the UI back while the browser leaves.
 */
export type RetryLatch = { decision?: Promise<boolean> };

type Deps = {
    storage: StorageLike;
    redirect: () => Promise<void>;
    latch: RetryLatch;
};

const pageLatch: RetryLatch = {};

const defaultDeps = (): Deps => ({
    storage: window.sessionStorage,
    redirect: () => signInWithRedirect({ provider: "Google" }),
    latch: pageLatch,
});

export const isFirstSignInAfterLinkError = (
    message: string | null | undefined,
): boolean => !!message && FIRST_SIGN_IN_AFTER_LINK.test(message);

/** Cognito's `error_description` from the URL it redirected back to, if any. */
export const oauthErrorDescriptionFromUrl = (
    search: string = window.location.search,
): string | null => new URLSearchParams(search).get("error_description");

/** The message inside a `signInWithRedirect_failure` Hub payload (`{ error }`). */
export const failureMessageOf = (payloadData: unknown): string | null => {
    const error =
        payloadData && typeof payloadData === "object" && "error" in payloadData
            ? (payloadData as { error: unknown }).error
            : payloadData;
    if (error && typeof error === "object" && "message" in error) {
        const { message } = error as { message?: unknown };
        return typeof message === "string" ? message : null;
    }
    return null;
};

/**
 * Send the browser back to Google once when the failure is the rejection of
 * the first sign-in after a link. Resolves true when the redirect is under
 * way (keep the loading state up), false when the failure stands. Never
 * retries twice in a row: the marker set before a retry is consumed by the
 * next failure, so a sign-in that keeps failing still reaches the user.
 */
export function retryGoogleSignInAfterLink(
    message: string | null | undefined,
    deps: Deps = defaultDeps(),
): Promise<boolean> {
    deps.latch.decision ??= decide(message, deps);
    return deps.latch.decision;
}

async function decide(
    message: string | null | undefined,
    { storage, redirect }: Deps,
): Promise<boolean> {
    if (!isFirstSignInAfterLinkError(message)) {
        return false;
    }

    try {
        if (storage.getItem(RETRY_FLAG) !== null) {
            storage.removeItem(RETRY_FLAG);
            return false;
        }
        storage.setItem(RETRY_FLAG, "1");
    } catch {
        // No session storage means no way to bound the retries: don't.
        return false;
    }

    try {
        await redirect();
        return true;
    } catch {
        clearGoogleSignInRetry(storage);
        return false;
    }
}

/** Forget the retry marker once a sign-in completes. */
export function clearGoogleSignInRetry(
    storage: StorageLike | undefined = globalThis.sessionStorage,
): void {
    try {
        storage?.removeItem(RETRY_FLAG);
    } catch {
        // Storage unavailable: nothing to forget.
    }
}
